/**
 * Le fil de la séance du tuteur, côté données : la séance (`liveSession.users` et leurs conversations avec le
 * champion) mise en formes de présentation.
 *
 * Ports littéraux :
 *  - les résumés du champion de `common/liveMode/AgentSummaryModal.js` : la liste (sauf un démarrage encore en
 *    cours, `starting_block` / `starting_block_study_program`), la plus récente d'abord, « NOUVEAU » sur les n
 *    premières (n = le compteur du champion à l'ouverture), le titre, la puce, l'avis demandé seulement sur une
 *    conversation finie dont le dernier message n'attend pas de réponse (`showFeedbackButtons && isLastMessage`),
 *    les photos (`attachments | checkpoint_attachments | student_response_attachments | media`) ;
 *  - le statut d'une activité rendue, lu dans les listes de l'élève (`activities`, `todo_activities`) : la carte
 *    du rendu vit tant qu'il attend validation (tech-derisk, point 12).
 */
import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { type UICorrectionFeedback } from "../../../ui/types/studentActivity"
import { type UITutorSamSummary } from "../../../ui/types/tutorLive"

export type ApiAgentConversation = {
    id?: string | number
    student_id?: string | number
    live_session_id?: string | number
    activity_id?: string | number | null
    status?: string | null
    trigger_context?: string | null
    summary_title?: string | null
    summary?: string | null
    tutor_needed?: boolean | null
    created_at?: string | null
    ended_at?: string | null
    tutor_feedback?: number | null
    agent_message?: { proposed_responses?: unknown[] | null } | null
    attachments?: string[] | null
    checkpoint_attachments?: string[] | null
    student_response_attachments?: string[] | null
    media?: string[] | null
}

export type ApiLiveSessionUser = {
    id?: string | number
    is_tutor?: boolean | null
    firstname?: string | null
    display_name?: string | null
    conversations?: ApiAgentConversation[] | null
}

export type ApiStudentActivity = {
    id?: string | number
    status?: string | null
    school_subject_id?: string | number | null
    material?: { id?: string | number | null; title?: string | null; school_subject_id?: string | number | null } | null
    student_chapter?: { chapter?: { id?: string | number | null } | null } | null
}
export type ApiStudentLists = {
    activities?: ApiStudentActivity[] | null
    todo_activities?: ApiStudentActivity[] | null
}

const STARTING_BLOCKS = new Set(["starting_block", "starting_block_study_program"])
const FEEDBACK_STATUSES = new Set(["ended", "tutor_needed", "new_activity_generation"])

/** Un démarrage encore en cours ne compte pas (`AgentSummaryModal.agentSummaries`, le réducteur du compteur). */
export function isCountedConversation(conversation: ApiAgentConversation): boolean {
    return !(conversation.status === "ongoing" && STARTING_BLOCKS.has(conversation.trigger_context ?? ""))
}

function time(conversation: ApiAgentConversation): number {
    const at = Date.parse(conversation.created_at ?? "")
    return Number.isNaN(at) ? 0 : at
}

/** « 17:20 », l'heure locale sur deux chiffres. */
export function clockOf(ms: number): string {
    const date = new Date(ms)
    const pad = (value: number) => String(value).padStart(2, "0")
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function feedbackOf(conversation: ApiAgentConversation, given?: number): UICorrectionFeedback | null | undefined {
    const value = given ?? conversation.tutor_feedback
    if (value === 1) return "positive"
    if (value === -1) return "negative"
    const asked =
        FEEDBACK_STATUSES.has(conversation.status ?? "") &&
        (conversation.agent_message?.proposed_responses == null ||
            conversation.agent_message.proposed_responses.length === 0)
    return asked ? null : undefined
}

/** Les photos d'un échange, adresses nettoyées (`renderAttachments`). */
export function conversationPhotos(conversation: ApiAgentConversation): string[] {
    const list =
        conversation.attachments ??
        conversation.checkpoint_attachments ??
        conversation.student_response_attachments ??
        conversation.media ??
        []
    return list.map((url) => cleanMediaUrl(url)).filter((url): url is string => url != null)
}

/**
 * Les résumés du champion avec un élève (`AgentSummaryModal`) : `newCount` est le compteur lu à l'ouverture,
 * `given` les avis donnés pendant cette ouverture (`feedbacks`).
 */
export function toUITutorSamSummaries(
    conversations: ApiAgentConversation[] | null | undefined,
    options: { newCount?: number; given?: Record<string, number> } = {}
): UITutorSamSummary[] {
    const newCount = options.newCount ?? 0
    return (conversations ?? [])
        .filter(isCountedConversation)
        .filter((conversation) => conversation.id != null)
        .sort((a, b) => time(b) - time(a))
        .map((conversation, index): UITutorSamSummary => {
            const ongoing = conversation.status === "ongoing"
            return {
                id: String(conversation.id),
                title: ongoing ? "Discussion non terminée" : conversation.summary_title || "Discussion terminée",
                time: clockOf(time(conversation)),
                status: ongoing ? "unfinished" : conversation.status === "interrupted" ? "interrupted" : "finished",
                isNew: index < newCount || undefined,
                needsHelp: conversation.status === "tutor_needed" || undefined,
                generatedActivity: conversation.status === "new_activity_generation" || undefined,
                summary: conversation.summary ?? "",
                photos: conversationPhotos(conversation),
                feedback: feedbackOf(conversation, options.given?.[String(conversation.id)]),
            }
        })
}

/** Les élèves inscrits de la séance (`liveSession.users` sans les tuteurs), en chaînes. */
export function enrolledStudents(users: ApiLiveSessionUser[] | null | undefined): ApiLiveSessionUser[] {
    return (users ?? []).filter((user) => user?.id != null && user.is_tutor !== true)
}

/**
 * Les résumés finis de cette séance, une ligne du champion chacun dans l'historique du fil (`helper`). Ils se
 * relisent du serveur (`origin: server`) : ils suivent sur un autre appareil.
 */
export type TutorHelperLine = { id: string; studentId: string; at: number; title?: string; text: string }

export function helperLines(users: ApiLiveSessionUser[] | null | undefined, liveSessionId: string): TutorHelperLine[] {
    const lines: TutorHelperLine[] = []
    for (const user of enrolledStudents(users)) {
        for (const conversation of user.conversations ?? []) {
            if (String(conversation.live_session_id) !== liveSessionId) continue
            if (conversation.status === "ongoing" || !conversation.summary?.trim() || conversation.id == null) continue
            const ended = Date.parse(conversation.ended_at ?? conversation.created_at ?? "")
            lines.push({
                id: `helper-${conversation.id}`,
                studentId: String(user.id),
                at: Number.isNaN(ended) ? 0 : ended,
                title: conversation.summary_title ?? undefined,
                text: conversation.summary,
            })
        }
    }
    return lines
}

/**
 * Le chapitre d'une activité de l'élève (`student_chapter.chapter`) et sa matière : la photo du chat que le tuteur
 * transforme en activité reprend le chapitre de celle que l'élève a devant lui. Aucun chapitre : `null`.
 */
export function activityChapter(
    student: ApiStudentLists | null | undefined,
    activityId: string | undefined
): { chapterId: string; subjectId: string | null } | null {
    if (student == null || activityId == null) return null
    const all = [...(student.activities ?? []), ...(student.todo_activities ?? [])]
    const activity = all.find((candidate) => String(candidate.id) === activityId)
    const chapterId = activity?.student_chapter?.chapter?.id
    if (chapterId == null) return null
    const subjectId = activity?.school_subject_id ?? activity?.material?.school_subject_id
    return { chapterId: String(chapterId), subjectId: subjectId != null ? String(subjectId) : null }
}

/** Le statut d'une activité de l'élève, là où ses listes la portent ; `null` si on ne la connaît pas. */
export function activityReviewStatus(
    student: ApiStudentLists | null | undefined,
    activityId: string | undefined
): string | null {
    if (student == null || activityId == null) return null
    const all = [...(student.activities ?? []), ...(student.todo_activities ?? [])]
    return all.find((activity) => String(activity.id) === activityId)?.status ?? null
}
